import React, { useEffect } from "react";
import { CreditCard, DollarSign, Download, ShoppingBag } from "lucide-react";

import { agruparOutros, corDaFatia, DonutGrafico } from "@/components/charts/Donut";
import { Button } from "@/components/ui/button";

import FiltroPeriodo from "../components/FiltroPeriodo";
import { CardResumo, Vazio } from "../components/Estados";
import { Esmaecer, EsqueletoPagamentos } from "../components/Esqueletos";
import { buscarDetalhado } from "../lib/api";
import { exportarExcel } from "../lib/excel";
import { formatCurrency, formatInt } from "../lib/formato";
import { rotuloArquivo } from "../lib/periodo";
import { useDados, usePeriodo } from "../lib/usePeriodo";

const pctDe = (valor, total) => (total > 0 ? ((valor / total) * 100).toFixed(1).replace(".", ",") : "0");

// Tabela que faz de legenda do donut: tudo o que antes ficava no "Detalhamento"
// agora mora aqui (pedido do dono, 21/09/2026).
function TabelaLegenda({ formas, total, totalPedidos }) {
  const th = "py-2 px-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold";
  return (
    <table className="w-full text-sm" data-e2e="tabela-pagamentos">
      <thead>
        <tr className="border-b">
          <th className={`text-left ${th}`}>Forma de pagamento</th>
          <th className={`text-right ${th}`}>Pedidos</th>
          <th className={`text-right ${th}`}>Valor movimentado</th>
          <th className={`text-right ${th}`}>% do total</th>
        </tr>
      </thead>
      <tbody>
        {formas.map((f, i) => (
          <tr key={f.name} className="border-b border-muted/50 hover:bg-muted/30">
            <td className="py-2 px-2 font-medium">
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: corDaFatia(f, i) }} />{f.name}</span>
            </td>
            <td className="py-2 px-2 text-right tabular-nums">{formatInt(f.quantidade)}</td>
            <td className="py-2 px-2 text-right font-semibold tabular-nums text-green-600 dark:text-green-400">{formatCurrency(f.valor)}</td>
            <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{pctDe(f.valor, total)}%</td>
          </tr>
        ))}
        <tr className="border-t-2 font-bold">
          <td className="py-2 px-2">Total</td>
          <td className="py-2 px-2 text-right tabular-nums">{formatInt(totalPedidos)}</td>
          <td className="py-2 px-2 text-right tabular-nums text-green-600 dark:text-green-400">{formatCurrency(total)}</td>
          <td className="py-2 px-2 text-right">100%</td>
        </tr>
      </tbody>
    </table>
  );
}

const COLUNAS_EXCEL = (total) => [
  { header: "Forma de Pagamento", value: (r) => r.name },
  { header: "Pedidos", value: (r) => r.quantidade },
  { header: "Valor (R$)", value: (r) => r.valor },
  { header: "% do Total", value: (r) => `${pctDe(r.valor, total)}%` },
];

const carregarPagamentos = (periodo) => buscarDetalhado("pagamentos", periodo);

// Relatórios → Relatório de Pagamentos.
export default function PagamentosTab({ definirAtualizar }) {
  const p = usePeriodo();
  const { dados, carregando, carregou, recarregar } = useDados(carregarPagamentos, p.periodo, { rotulo: "os pagamentos" });
  useEffect(() => { definirAtualizar({ recarregar, carregando }); }, [definirAtualizar, recarregar, carregando]);

  const lista = dados?.formas_pagamento || [];
  const total = dados?.total_valor || 0;
  const totalPedidos = lista.reduce((s, f) => s + (f.quantidade || 0), 0);
  const formas = agruparOutros(lista, "valor", 10, ["quantidade"]);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0"><FiltroPeriodo {...p} /></div>
        {lista.length > 0 && (
          <Button variant="outline" size="sm" className="gap-2 shrink-0"
            onClick={() => exportarExcel(lista, COLUNAS_EXCEL(total), `Pagamentos_${rotuloArquivo(p.periodo)}.xlsx`)}>
            <Download className="w-4 h-4" /> Excel
          </Button>
        )}
      </div>
      {!carregou ? <EsqueletoPagamentos /> : (
        <Esmaecer ativo={carregando}>
          {!lista.length ? <Vazio texto="Nenhum dado de pagamento no período" /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CardResumo label="Formas de Pagamento" value={formatInt(lista.length)} icon={CreditCard} />
            <CardResumo label="Total de Pedidos" value={formatInt(totalPedidos)} icon={ShoppingBag} />
            <CardResumo label="Faturamento Total" value={formatCurrency(total)} icon={DollarSign} />
          </div>
          <div className="bg-card rounded-xl border p-6" data-e2e="distribuicao-pagamentos">
            <h2 className="text-xl font-bold mb-4">Distribuição por Forma de Pagamento</h2>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
              <div className="lg:col-span-2">
                <DonutGrafico data={formas} dataKey="valor" height={280}
                  formatVal={(v, item) => `${formatCurrency(v)}${item.quantidade != null ? ` · ${formatInt(item.quantidade)} pedido(s)` : ""}`} />
              </div>
              <div className="lg:col-span-3 overflow-x-auto"><TabelaLegenda formas={formas} total={total} totalPedidos={totalPedidos} /></div>
            </div>
          </div>
        </>
          )}
        </Esmaecer>
      )}
    </div>
  );
}
